import { REAL_TIME_COMMUNICATION_PLATFORMS } from '@this+that/constants';
import {
  AgentDelegationKind,
  InboxEntry,
  MessagingPlatform,
  UserCommunication,
} from '@this+that/models';

import type {
  DelegateInputContext,
  DoBoxInputContext,
} from '../../../../store/FloatingAssistant/types';

const EMAIL_PREVIEW_MAX_LENGTH = 120;
const DOBOX_PREVIEW_MAX_LENGTH = 120;

function resolveSourceFromPlatform(
  platform: MessagingPlatform | null | undefined,
): DelegateInputContext['source'] {
  if (
    platform != null &&
    REAL_TIME_COMMUNICATION_PLATFORMS.includes(platform)
  ) {
    return 'slack';
  }
  return 'email';
}

export function buildEmailDelegateContext(
  userCommunication: UserCommunication,
): DelegateInputContext {
  return {
    kind: 'delegate',
    source: resolveSourceFromPlatform(userCommunication.platform),
    itemId: String(userCommunication.messageId),
    author: userCommunication.author ?? userCommunication.authorEmail ?? '',
    subject: userCommunication.subject ?? undefined,
    preview:
      userCommunication.messagePreview ??
      userCommunication.messageText?.slice(0, EMAIL_PREVIEW_MAX_LENGTH) ??
      '',
  };
}

// Only a delegation the current user started, with a conversation already running,
// carries its custom agent back into the hosted panel — otherwise a follow-up sent
// through this context would silently fall back to the default assistant persona
// (WEB-P0.04).
function resolveFollowUpAgentContext(
  inboxEntry: InboxEntry,
  userId: string | undefined,
): Pick<DoBoxInputContext, 'agentTemplateId' | 'delegationConversationId'> {
  const delegation = inboxEntry.agentDelegation;
  if (
    delegation?.kind !== AgentDelegationKind.AGENT_RUN ||
    delegation.agentTemplateId == null ||
    delegation.conversationId == null ||
    delegation.delegatedByUserId !== userId
  ) {
    return {};
  }
  return {
    agentTemplateId: delegation.agentTemplateId,
    delegationConversationId: delegation.conversationId,
  };
}

export function buildDoBoxDelegateContext(
  inboxEntry: InboxEntry,
  userId?: string,
): DoBoxInputContext {
  return {
    kind: 'dobox',
    itemId: inboxEntry.id,
    taskGroupId: inboxEntry.taskGroupId,
    subject: inboxEntry.subject ?? '',
    preview: (inboxEntry.description ?? inboxEntry.subject ?? '').slice(
      0,
      DOBOX_PREVIEW_MAX_LENGTH,
    ),
    ...resolveFollowUpAgentContext(inboxEntry, userId),
  };
}
